/**
 * Mobile Bottom Bar Component
 * Handles actions for Menu drawer, Search modal, and Cart drawer.
 * Keeps cart badge count synchronized in real time.
 */

class MobileBottomBar extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-action]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const action = button.getAttribute('data-action');
        this.handleAction(action);
      });
    });

    // Listen to all cart events for real-time badge count updates
    document.addEventListener('cart:update', this.updateCartBadge.bind(this));
    document.addEventListener('cartLinesUpdate', this.updateCartBadge.bind(this));
    document.addEventListener('cart:item-added', this.updateCartBadge.bind(this));
    window.addEventListener('cart:item-added', this.updateCartBadge.bind(this));
    window.addEventListener('pageshow', this.updateCartBadge.bind(this));

    // Initial check
    this.updateCartBadge();
  }

  handleAction(action) {
    if (action === 'menu') {
      this.toggleMenuDrawer();
    } else if (action === 'search') {
      this.toggleSearchModal();
    } else if (action === 'cart') {
      this.toggleCartDrawer();
    }
  }

  toggleMenuDrawer() {
    // Trigger the native header drawer summary directly
    const headerSummary = document.querySelector(
      'header-drawer details summary, .header__icon--menu, summary.header__icon--summary, [aria-controls="Details-menu-drawer-container"]'
    );
    if (headerSummary) {
      headerSummary.click();
      return;
    }

    // Fallback: Check if there is an instance of header-drawer custom element
    const headerDrawer = document.querySelector('header-drawer');
    if (headerDrawer && typeof headerDrawer.toggle === 'function') {
      headerDrawer.toggle();
      return;
    }
  }

  toggleSearchModal() {
    // 1. Look for search modal trigger in header or DOM
    const searchTrigger = document.querySelector(
      '[data-testid="search-action"], [on\\:click*="search-modal/open"], [aria-controls="search-modal"], button.header__icon--search'
    );
    if (searchTrigger) {
      searchTrigger.click();
      return;
    }

    // 2. Look for dialog directly
    const searchModal = document.querySelector('dialog-component#search-modal, #search-modal');
    if (searchModal) {
      const dialog = searchModal.querySelector('dialog');
      if (dialog) {
        if (dialog.open) {
          dialog.close();
        } else {
          dialog.showModal();
          const input = dialog.querySelector('input[type="search"]');
          if (input) setTimeout(() => input.focus(), 60);
        }
        return;
      }
    }

    // Fallback: navigate to search page
    window.location.href = '/search';
  }

  toggleCartDrawer() {
    // 1. Try theme-drawer #cart-drawer open/close method
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer, #cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function' && typeof cartDrawer.close === 'function') {
        if (cartDrawer.isOpen) {
          cartDrawer.close();
        } else {
          cartDrawer.open();
        }
        return;
      }
      const dialog = cartDrawer.querySelector('dialog');
      if (dialog) {
        if (dialog.open) {
          dialog.close();
        } else {
          dialog.showModal();
        }
        return;
      }
    }

    // 2. Try clicking the header cart trigger
    const headerCartBtn = document.querySelector('[data-testid="cart-drawer-trigger"], [on\\:click*="#cart-drawer/toggle"]');
    if (headerCartBtn) {
      headerCartBtn.click();
      return;
    }

    // 3. Fallback: navigate to cart page
    window.location.href = '/cart';
  }

  updateCartBadge() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const count = cart.item_count || 0;
        const bubbles = this.querySelectorAll('.cart-bubble');
        bubbles.forEach(bubble => {
          const countSpan = bubble.querySelector('.cart-bubble__text-count');
          if (countSpan) {
            countSpan.textContent = count < 100 ? String(count) : '99+';
            if (count > 0) {
              countSpan.classList.remove('hidden');
              bubble.classList.remove('visually-hidden');
              bubble.style.display = 'flex';
            } else {
              countSpan.classList.add('hidden');
              bubble.classList.add('visually-hidden');
              bubble.style.display = 'none';
            }
          }
        });
      })
      .catch(() => {});
  }
}

if (!customElements.get('mobile-bottom-bar')) {
  customElements.define('mobile-bottom-bar', MobileBottomBar);
}
